/**
 * Get Fleet Summary Use Case
 * Fetches fleet overview data including machine counts and connector status
 */

import { DashboardRepository } from '../data/dashboard-repository';
import { FleetSummary, ConnectorStatusBreakdown, SummaryPeriod } from '../types/dashboard-types';
import { FleetTransformer } from '../transformers/FleetTransformer';

export class GetFleetSummaryUseCase {
  constructor(private readonly repository: DashboardRepository) {}

  async execute(
    period?: SummaryPeriod,
    customDateRange?: { from: Date; to: Date }
  ): Promise<{
    summary: FleetSummary;
    connectorBreakdown: ConnectorStatusBreakdown[];
    onlinePercentage: number;
    chargingPercentage: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      const summary = await this.repository.getFleetSummary(undefined, period, customDateRange);

      // Calculate derived data
      const connectorBreakdown = FleetTransformer.calculateConnectorBreakdown(summary);
      const onlinePercentage = FleetTransformer.calculateOnlinePercentage(summary);
      const chargingPercentage = FleetTransformer.calculateChargingPercentage(summary);

      return {
        summary,
        connectorBreakdown,
        onlinePercentage,
        chargingPercentage,
        hasError: false,
      };
    } catch (error) {
      console.error('GetFleetSummaryUseCase.execute failed:', error);

      return {
        summary: this.getEmptySummary(),
        connectorBreakdown: [],
        onlinePercentage: 0,
        chargingPercentage: 0,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load fleet summary',
      };
    }
  }

  private getEmptySummary(): FleetSummary {
    return {
      // Machine counts by OCPP status
      totalMachines: 0,
      availableMachines: 0,
      preparingMachines: 0,
      chargingMachines: 0,
      finishingMachines: 0,
      suspendedEVMachines: 0,
      suspendedEVSEMachines: 0,
      faultedMachines: 0,
      unavailableMachines: 0,
      reservedMachines: 0,

      // Connector counts by OCPP status
      totalConnectors: 0,
      availableConnectors: 0,
      preparingConnectors: 0,
      chargingConnectors: 0,
      finishingConnectors: 0,
      suspendedEVConnectors: 0,
      suspendedEVSEConnectors: 0,
      faultedConnectors: 0,
      unavailableConnectors: 0,
      reservedConnectors: 0,

      // Period-filtered metrics
      totalEnergySupplied: 0,
      totalSessions: 0,
      totalBuyEnergy: 0,
      totalSaleEnergy: 0,
      totalRevEnergy: 0,

      // Legacy fields
      totalEnergyToday: 0,
      totalRevenueToday: 0,
      activeSessionsCount: 0,

      lastUpdated: new Date(),
    };
  }
}
